/* File: src\components\replay\ReplayStatsPanel.js */

import React from 'react';
import PropTypes from 'prop-types';

// Helper to format percentage (expects 0-1 value)
const formatPercent = (value) => {
    if (typeof value !== 'number' || isNaN(value)) {
        return 'N/A';
    }
    // Multiply by 100 before rounding for display
    return `${Math.round(value * 100)}%`;
};

// Helper to determine accuracy color based on the new logic (expects 0-1 value)
const getAccuracyColor = (accuracy) => {
    if (typeof accuracy !== 'number' || isNaN(accuracy)) {
        return 'text-gray-700'; // Default color for N/A or invalid data
    }
    // --- THRESHOLD CHECK ---
    // Using 0.7 (70%) as the threshold for green
    if (accuracy >= 0.7) {
        return 'text-green-600'; // Good accuracy (70%+)
    }
    // --- LOW ACCURACY CHECK ---
    // Only show red if accuracy is positive but below the threshold
    if (accuracy > 0) {
        return 'text-red-600';   // Low accuracy (below 70% but above 0%)
    }
    // --- DEFAULT/ZERO CHECK ---
    // Otherwise (accuracy is exactly 0 or potentially negative), use default text color
    return 'text-gray-700';      // Default color for 0% or invalid negative values
};


// Individual stat card component
const StatCard = ({ label, value, details, colorClass = 'text-gray-700' }) => (
    <div className="bg-gray-50 p-3 rounded-lg text-center shadow-sm border border-gray-200">
        <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
        <p className={`text-xl sm:text-2xl font-bold ${colorClass}`}>{value}</p>
        {details && <p className="text-xs text-gray-500 mt-1">{details}</p>}
    </div>
);

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    details: PropTypes.string,
    colorClass: PropTypes.string,
};

/**
 * Displays live performance statistics during replay.
 * Uses a 2x2 grid layout with updated styling and color logic.
 */
const ReplayStatsPanel = ({ stats }) => {
    // Return null if no stats are available to prevent rendering an empty box
    // Use initialStatsState for defaults if stats is null/undefined briefly
    const currentStats = stats || initialStatsState;

    // Destructure stats with default values from the initial state structure
    const {
        visualAccuracy,
        audioAccuracy,
        visualCorrect,
        visualTotal, // Now represents Hits + Misses
        audioCorrect,
        audioTotal, // Now represents Hits + Misses
        missedVisual,
        missedAudio,
        falseAlarmVisual,
        falseAlarmAudio,
    } = currentStats;

    // Determine the color for each stat card using helper functions or direct logic
    const visualColor = getAccuracyColor(visualAccuracy); // Pass the 0-1 fraction
    const audioColor = getAccuracyColor(audioAccuracy);   // Pass the 0-1 fraction
    const missedColor = (missedVisual + missedAudio) > 0 ? 'text-orange-600' : 'text-gray-700';
    const falseAlarmColor = (falseAlarmVisual + falseAlarmAudio) > 0 ? 'text-purple-600' : 'text-gray-700';

    return (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Live Performance Summary</h3>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <StatCard
                    label="Visual Accuracy"
                    value={formatPercent(visualAccuracy)} // Format the 0-1 fraction
                    details={`${visualCorrect} / ${visualTotal} correct`} // Show Hits / (Hits + Misses)
                    colorClass={visualColor}
                />
                <StatCard
                    label="Audio Accuracy"
                    value={formatPercent(audioAccuracy)} // Format the 0-1 fraction
                    details={`${audioCorrect} / ${audioTotal} correct`} // Show Hits / (Hits + Misses)
                    colorClass={audioColor}
                />
                 <StatCard
                    label="Missed Matches"
                    value={missedVisual + missedAudio}
                    details={`Visual: ${missedVisual} / Audio: ${missedAudio}`}
                    colorClass={missedColor}
                />
                <StatCard
                    label="False Alarms"
                    value={falseAlarmVisual + falseAlarmAudio}
                    details={`Visual: ${falseAlarmVisual} / Audio: ${falseAlarmAudio}`}
                    colorClass={falseAlarmColor}
                />
            </div>
        </div>
    );
};

// Prop types should match the corrected structure from useReplayStats
ReplayStatsPanel.propTypes = {
    stats: PropTypes.shape({
        visualAccuracy: PropTypes.number,
        audioAccuracy: PropTypes.number,
        visualCorrect: PropTypes.number,
        visualTotal: PropTypes.number,
        audioCorrect: PropTypes.number,
        audioTotal: PropTypes.number,
        missedVisual: PropTypes.number,
        missedAudio: PropTypes.number,
        falseAlarmVisual: PropTypes.number,
        falseAlarmAudio: PropTypes.number,
    }),
};

export default ReplayStatsPanel;